import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Checkbox, FormControlLabel, Divider } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SortIcon from '@mui/icons-material/Sort';
import { RMSButton } from '../../../buttons';
import { EMPTY_COLUMNS, EMPTY_MODEL } from '../const/dataGridConstants.js';

const ColumnSelector = ({ columnOrdering, columnVisibility }) => {
  const { orderedColumns = EMPTY_COLUMNS, setColumnOrder, reorderEnabled = false, toggleReorder } =
    columnOrdering || {};
  const { model = EMPTY_MODEL, setModel } = columnVisibility || {};

  const dragFieldRef = useRef(null);
  const [dragOverField, setDragOverField] = useState(null);
  const listableColumns = useMemo(
    () =>
      orderedColumns.filter(
        (col) => col.field !== 'actions' && col.field !== '__check__' && col.hideable !== false
      ),
    [orderedColumns]
  );

  const handleToggle = useCallback(
    (field) => {
      const isCurrentlyVisible = model[field] !== false;
      setModel?.((prev) => ({
        ...prev,
        [field]: isCurrentlyVisible ? false : true,
      }));
    },
    [model, setModel]
  );

  const handleShowAll = useCallback(() => {
    setModel?.((prev) => {
      const next = { ...prev };
      listableColumns.forEach((col) => {
        next[col.field] = true;
      });
      return next;
    });
  }, [listableColumns, setModel]);

  const handleHideAll = useCallback(() => {
    setModel?.((prev) => {
      const next = { ...prev };
      listableColumns.forEach((col) => {
        next[col.field] = false;
      });
      return next;
    });
  }, [listableColumns, setModel]);

  const handleDragStart = useCallback((field) => {
    dragFieldRef.current = field;
  }, []);

  const handleDragOver = useCallback(
    (e, field) => {
      e.preventDefault();
      if (field !== dragOverField) setDragOverField(field);
    },
    [dragOverField]
  );

  const handleDrop = useCallback(
    (targetField) => {
      const sourceField = dragFieldRef.current;
      dragFieldRef.current = null;
      setDragOverField(null);

      if (!sourceField || sourceField === targetField) return;

      setColumnOrder?.((prev) => {
        const safePrev =
          Array.isArray(prev) && prev.length ? prev : orderedColumns.map((c) => c.field);

        const newOrder = [...safePrev];
        const fromIndex = newOrder.indexOf(sourceField);
        const toIndex = newOrder.indexOf(targetField);

        if (fromIndex === -1 || toIndex === -1) return safePrev;

        newOrder.splice(fromIndex, 1);
        newOrder.splice(toIndex, 0, sourceField);

        return newOrder;
      });
    },
    [orderedColumns, setColumnOrder]
  );

  return (
    <div className="columns-panel">
      <div className="columns-panel__actions">
        <RMSButton
          size="small"
          label="Show All"
          color="default"
          variant="text"
          onClick={handleShowAll}
        />
        <RMSButton
          size="small"
          label="Hide All"
          color="default"
          variant="text"
          onClick={handleHideAll}
        />
      </div>

      <RMSButton
        size="small"
        fullWidth
        variant={reorderEnabled ? 'contained' : 'outlined'}
        startIcon={<SortIcon fontSize="small" />}
        onClick={toggleReorder}
        className="columns-panel__sort-btn"
        label={reorderEnabled ? 'Column Sorting: On' : 'Column Sorting: Off'}></RMSButton>

      <Divider className="columns-panel__divider" />

      {listableColumns.map((col) => (
        <div
          key={col.field}
          draggable
          onDragStart={() => handleDragStart(col.field)}
          onDragOver={(e) => handleDragOver(e, col.field)}
          onDrop={() => handleDrop(col.field)}
          className={`columns-panel__row${
            dragOverField === col.field ? ' columns-panel__row--drag-over' : ''
          }`}>
          <DragIndicatorIcon fontSize="small" className="columns-panel__drag-icon" />
          <FormControlLabel
            className="columns-panel__checkbox-label"
            control={
              <Checkbox
                size="small"
                checked={model[col.field] !== false}
                onChange={() => handleToggle(col.field)}
              />
            }
            label={col.headerName || col.field}
          />
        </div>
      ))}
    </div>
  );
};

export default React.memo(ColumnSelector);
